// A link to a file the server sends (a PDF, a document, a CSV) that cannot
// strand somebody using Dayboard as a Home Screen app on an iPhone.
//
// A Home Screen app has no address bar and no back button. A plain link to a
// file replaces the whole app with that file, and there is no way out short of
// closing the app (Nathan, 2026-10-09, after opening a timesheet's PDF). So on
// an iPhone running from the Home Screen, and ONLY there, the tap fetches the
// file (same origin, the session cookie rides along) and hands it to the
// share sheet, which saves, prints, mails or opens it and then comes back to
// the app. Everywhere else it is the plain link it always was: a browser
// tab has a back button, and Android saves a download without leaving.
//
// Safari can refuse the share sheet when the fetch took long enough that the
// tap no longer counts as the person's own; the file is then already in hand,
// so the link asks for one more tap and shares it at once.
import { useState, type MouseEvent, type ReactNode } from 'react';

/** True for an iPhone or iPad running the site from its Home Screen. iOS only: no other browser sets it. */
export function isIosHomeScreenApp(): boolean {
  return typeof navigator !== 'undefined' && (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** The filename the server named in Content-Disposition, preferring the UTF-8 form. */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1]!.trim());
    } catch {
      // A malformed escape: fall through to the plain form.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/i.exec(header);
  const name = (plain?.[1] ?? plain?.[2] ?? '').trim();
  return name || fallback;
}

type State = { kind: 'idle' } | { kind: 'fetching' } | { kind: 'ready'; file: File } | { kind: 'failed' };

export function DownloadLink({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const [state, setState] = useState<State>({ kind: 'idle' });

  async function share(file: File) {
    try {
      await navigator.share({ files: [file] });
      setState({ kind: 'idle' });
    } catch (err) {
      // A DOMException, which is not an Error in every engine: read the name.
      const name = (err as { name?: unknown } | null)?.name;
      if (name === 'AbortError') setState({ kind: 'idle' }); // closed the sheet
      else if (name === 'NotAllowedError') setState({ kind: 'ready', file }); // tap went stale
      else setState({ kind: 'failed' });
    }
  }

  async function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (!isIosHomeScreenApp() || typeof navigator.share !== 'function') return;
    event.preventDefault();
    if (state.kind === 'fetching') return;
    if (state.kind === 'ready') return void share(state.file);

    setState({ kind: 'fetching' });
    try {
      const res = await fetch(href, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const type = (res.headers.get('Content-Type') ?? '').split(';')[0]!.trim() || 'application/octet-stream';
      const file = new File([await res.arrayBuffer()], filenameFromDisposition(res.headers.get('Content-Disposition'), 'download'), {
        type,
      });
      if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
        setState({ kind: 'failed' });
        return;
      }
      await share(file);
    } catch {
      setState({ kind: 'failed' });
    }
  }

  return (
    <>
      <a href={href} className={className} onClick={(event) => void handleClick(event)} aria-busy={state.kind === 'fetching' || undefined}>
        {state.kind === 'fetching' ? 'Getting it…' : state.kind === 'ready' ? 'Tap again to save' : children}
      </a>
      {state.kind === 'failed' && (
        <span role="alert" className="ml-2 text-sm text-red-700">
          Could not get that file. Check your signal and try again.
        </span>
      )}
    </>
  );
}
